import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Home as HomeIcon, Star } from "lucide-react";

export default function Directory() {
  const { t, catLabel, bizName, bizDesc, lang } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState(params.get("city") || "");
  const [state, setState] = useState(params.get("state") || "");
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "";
  const sort = params.get("sort") || "rating";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/categories/suggested").then((r) => setSuggested(r.data)).catch(() => {});
  }, []);

  const applySuggested = (label) => {
    const p = new URLSearchParams(params);
    p.set("q", label);
    p.delete("category");
    setQ(label);
    setParams(p);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/businesses", { params: Object.fromEntries(params) });
      setBusinesses(data);
    } catch { setBusinesses([]); }
    setLoading(false);
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => {
    const p = new URLSearchParams(params);
    city ? p.set("city", city) : p.delete("city");
    state ? p.set("state", state) : p.delete("state");
    q ? p.set("q", q) : p.delete("q");
    setParams(p);
  };

  const setCategory = (key) => {
    const p = new URLSearchParams(params);
    key ? p.set("category", key) : p.delete("category");
    setParams(p);
  };

  const setSort = (val) => {
    const p = new URLSearchParams(params);
    p.set("sort", val);
    setParams(p);
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-secondary">{t("nav_directory")}</h1>

      <div className="mt-6 flex flex-col gap-3 rounded-sm border border-slate-200 bg-white p-4 shadow-sm md:flex-row" data-testid="directory-filters">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} className="rounded-sm" data-testid="filter-q-input" />
        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("search_city")} className="rounded-sm md:max-w-[180px]" data-testid="filter-city-input" />
        <Input value={state} onChange={(e) => setState(e.target.value)} placeholder={t("search_state")} className="rounded-sm md:max-w-[140px]" data-testid="filter-state-input" />
        <Button onClick={applyFilters} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="filter-apply-btn"><Search className="mr-2 h-4 w-4" />{t("search_btn")}</Button>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setCategory("")} className={`rounded-sm border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${!category ? "border-primary bg-primary text-white" : "border-slate-200 text-slate-600 hover:border-primary"}`} data-testid="cat-filter-all">{t("all_categories")}</button>
          {categories.map((c) => (
            <button key={c.key} onClick={() => setCategory(c.key)} className={`rounded-sm border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${category === c.key ? "border-primary bg-primary text-white" : "border-slate-200 text-slate-600 hover:border-primary"}`} data-testid={`cat-filter-${c.key}`}>{catLabel(c)}</button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{t("sort_by")}</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-sm border border-slate-200 bg-white px-2 text-sm text-slate-700 outline-none" data-testid="sort-select">
            <option value="rating">{t("sort_rating")}</option>
            <option value="reviews">{t("sort_reviews")}</option>
            <option value="newest">{t("sort_newest")}</option>
            <option value="name">{t("sort_name")}</option>
          </select>
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="py-20 text-center text-slate-400">…</div>
        ) : businesses.length === 0 ? (
          <div className="rounded-sm border border-dashed border-slate-300 py-20 text-center text-slate-500" data-testid="directory-empty">{t("no_results")}</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="directory-grid">
            {businesses.map((b) => (
              <div key={b.id} onClick={() => navigate(`/business/${b.id}`)} className="cursor-pointer overflow-hidden rounded-sm border border-slate-200 bg-white shadow-sm transition-transform hover:-translate-y-1 hover:shadow-lg" data-testid={`business-card-${b.id}`}>
                <div className="h-40 bg-slate-200">
                  {b.cover_image ? <img src={b.cover_image} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-slate-400"><HomeIcon className="h-10 w-10" /></div>}
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <h3 className="font-head text-lg font-bold text-secondary">{bizName(b)}</h3>
                    <span className="flex items-center gap-1 text-xs font-bold text-primary"><Star className="h-3.5 w-3.5 fill-primary" />{b.review_count ? `${b.rating} (${b.review_count})` : t("new_pro")}</span>
                  </div>
                  {(b.city || b.state) && <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {[b.city, b.state].filter(Boolean).join(", ")}</p>}
                  {((b.category === "other" && b.custom_category) || b.is_master) && (
                    <span className="mt-2 inline-block rounded-sm bg-primary/10 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-primary" data-testid={`biz-custom-cat-${b.id}`}>{b.is_master ? (lang === "es" ? "Todos los servicios" : "All services") : b.custom_category}</span>
                  )}
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{bizDesc(b)}</p>
                  <Button variant="outline" size="sm" className="mt-4 w-full rounded-sm">{t("view_profile")}</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
